import { lockScroll, unlockScroll } from './scrollLock';

// OneTrust's banner/preference-center close handlers ("Reject All", "Accept All",
// "Confirm My Choices", the X icon) intermittently fail to clear the `overflow: hidden`
// they apply while their overlay is open (third-party bug, cdn.cookielaw.org script),
// sometimes even re-applying it a moment *after* the overlay is already hidden. Rather
// than trusting OneTrust to clean up after itself, we track its overlay's actual visibility
// ourselves and reconcile the shared scroll lock, so a stuck lock can never permanently
// freeze page scrolling.
//
// OneTrust serves different consent templates/DOM structures depending on the visitor's
// detected region (e.g. a CCPA opt-out flow vs an IAB TCF banner vs a plain US banner), so
// we don't hardcode specific banner/preference-center element IDs. Instead we treat any
// descendant of the top-level `#onetrust-consent-sdk` container (besides the small,
// always-present floating "reopen preferences" icon) with non-zero rendered size as an
// active overlay, regardless of which template is being shown.
const LOCK_ID = 'onetrust';
const SDK_CONTAINER_ID = 'onetrust-consent-sdk';
const PERSISTENT_ELEMENT_IDS = new Set(['ot-sdk-btn-floating']);
let initialized = false;

function isVisible(el) {
  if (!el) return false;
  const style = getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function isAnyOverlayVisible(sdk) {
  return Array.from(sdk.children).some((child) => (
    !PERSISTENT_ELEMENT_IDS.has(child.id) && isVisible(child)
  ));
}

function syncLock() {
  const sdk = document.getElementById(SDK_CONTAINER_ID);
  if (sdk && isAnyOverlayVisible(sdk)) {
    lockScroll(LOCK_ID);
  } else {
    unlockScroll(LOCK_ID);
  }
}

export default function initOneTrustScrollLockWatcher() {
  if (initialized || typeof document === 'undefined') return;
  initialized = true;

  // Any attribute change (style/class) on the sdk container or any of its overlay children
  // can flip visibility, and children get added/removed as the user opens/closes overlays.
  // A single observer instance can watch multiple targets over time, which matters here
  // since #onetrust-consent-sdk can be torn down and rebuilt as a brand new node mid-session
  // (e.g. by OneTrust's own SDK reinitializing, or a future reload of it).
  const sdkObserver = new MutationObserver(syncLock);
  const watchedSdks = new WeakSet();

  function watchSdkContainer(sdk) {
    if (watchedSdks.has(sdk)) return;
    watchedSdks.add(sdk);
    sdkObserver.observe(sdk, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true,
    });
    syncLock();
  }

  function checkForSdkContainer() {
    const sdk = document.getElementById(SDK_CONTAINER_ID);
    if (sdk) watchSdkContainer(sdk);
  }

  checkForSdkContainer();

  // Keep watching indefinitely (never disconnect) so a later replacement of the sdk
  // container is always picked up, not just its first-ever appearance.
  const bodyChildObserver = new MutationObserver(checkForSdkContainer);
  bodyChildObserver.observe(document.body, { childList: true });

  // Re-check on every style mutation to <html> or <body> too, regardless of who triggered
  // it, in case a stuck lock is re-applied without any corresponding change inside the sdk
  // container (or a template locks scroll on <html> instead of/in addition to <body>).
  const rootStyleObserver = new MutationObserver(syncLock);
  rootStyleObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });
  rootStyleObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
}
